
/**
 * LimitedSelect.vue
 *
 * This wrapper component can be used to:
 *
 * - Wrap "v-select" for improved performance with large option lists.
 * - Filter options with a debounced search input.
 * - Forward props and slots for "v-select" compatibility.
 */

import { Component, Vue, Prop, Watch } from "vue-property-decorator"
import vSelect from "vue-select"

@Component({
	components: {
		"v-select": vSelect
	}
})
export default class LimitedSelect extends Vue {
	/* --- Props --- */

	@Prop()
	value!: any

	@Prop({ type: Array, required: true })
	options!: any[]

	@Prop({ type: Number, default: 100 })
	limit!: number

	@Prop({ type: String, default: "" })
	label!: string

	@Prop({ type: String, default: "" })
	placeholder!: string

	@Prop({ type: Function, default: (o: any) => o.id })
	reduce!: (o: any) => any

	@Prop({ type: Boolean, default: false })
	multiple!: boolean

	@Prop({ type: Boolean, default: false })
	clearable!: boolean

	@Prop({ type: Boolean, default: false })
	disabled!: boolean

	@Prop({ type: Boolean, default: false })
	required!: boolean

	@Prop({ type: Boolean, default: false })
	appendToBody!: boolean

	@Prop({ type: Boolean, default: false })
	noDrop!: boolean

	@Prop({ type: [Object, Array, Function], default: null })
	components!: any

	@Prop({ type: [String, Object, Array], default: () => ({}) })
	customClass!: string | object | any[]

	/* --- Data --- */

	private searchTerm: string = ""
	private debouncedSearchTerm: string = ""
	private debounceTimeout: NodeJS.Timeout | null = null
	private debounceSearchTermMs: number = 300

	private matchingNonSelectedCount: number = 0

	/* --- Watchers --- */

	@Watch("searchTerm")
	private onSearchTermChanged (newVal: string) {
		if (this.debounceTimeout) {
			clearTimeout(this.debounceTimeout)
		}

		this.debounceTimeout = setTimeout(() => {
			this.debouncedSearchTerm = newVal
		}, this.debounceSearchTermMs)
	}

	/* --- Computed Properties --- */

	/**
	 * Returns the options to display in the v-select component, ensuring:
	 * 1. Selected options are always included.
	 * 2. Non-selected options are filtered by searchTerm and limited in number.
	 * 3. An optionMap is built (keyed by reduce(option)) for efficient lookup of selected options.
	 *
	 * @returns An array of option objects, with selected options first and filtered/limited non-selected options after.
	 */
	private get limitedOptions (): any[] {
		const searchLower = this.debouncedSearchTerm.toLowerCase()
		const selectedValues = new Set(Array.isArray(this.value) ? this.value : [this.value])

		const selectedOptions: any[] = []
		const nonSelected: any[] = []
		const optionMap = new Map<any, any>()

		this.matchingNonSelectedCount = 0

		for (const option of this.options) {
			const key = this.reduce(option)
			optionMap.set(key, option)

			if (selectedValues.has(key)) {
				selectedOptions.push(option) // always include selected
			} else if (
				!this.debouncedSearchTerm ||
				String(option[this.label] || "").toLowerCase().includes(searchLower)
			) {
				nonSelected.push(option)
				this.matchingNonSelectedCount++
			}
		}

		const limitedNonSelected = nonSelected.slice(0, this.limit)
		return [...selectedOptions, ...limitedNonSelected]
	}

	/**
	 * Checks whether the total number of non-selected matching
	 * options exceeds the configured limit.
	 *
	 * @returns "true" if matchingNonSelectedCount > limit.
	 */
	private get hasExcessOptions (): boolean {
		return this.matchingNonSelectedCount > this.limit
	}

	/**
	 * Returns a note message for when the number of
	 * non-selected matching options exceeds the configured limit.
	 * Adjusts the message if a search/filter term is applied.
	 *
	 * @returns The note message string.
	 */
	private get limitedOptionsNote (): string {
		const hasFilter = !!this.debouncedSearchTerm
		return hasFilter
			? `Showing only the first ${this.limit} matching options. Start typing to filter more.`
			: `Showing only the first ${this.limit} options. Start typing to filter more.`
	}

	/* --- Methods --- */

	/**
	 * Updates the component's search term as the user types.
	 *
	 * @param term The current search input from the user.
	 */
	private onSearch (term: string) {
		this.searchTerm = term || ""
	}
}
